import React, { useEffect, useRef } from 'react';
import { RoadToolGeometry } from '../services/roadTool';
import { resolveApiUrl } from '../utils/api';
import axios from 'axios';

interface RoadToolOverlayProps {
  workzone: any[];
  map: google.maps.Map | null;
  geometry: RoadToolGeometry | null;
  visible: boolean;
  editable?: boolean;
  onGeometryChange?: (geometry: RoadToolGeometry) => void;
}

/**
 * RoadToolOverlay Component
 * 
 * Renders road tool geometry (centerlines, lane boundaries, road edges, intersections)
 * as a vector overlay on Google Maps. Styled similar to TGS field guide diagrams.
 * 
 * Features:
 * - Centerlines: Blue dashed lines
 * - Lane boundaries: Yellow solid lines
 * - Road edges: Gray solid lines
 * - Intersections: Red markers
 * - Toggleable visibility
 * - Future: Editable geometry
 */


export const RoadToolOverlay: React.FC<RoadToolOverlayProps> = ({
  workzone,
  map,
  geometry,
  visible,
  editable = false,
  onGeometryChange
}) => {
  const dataLayerRef = useRef<google.maps.Data | null>(null);
  const centerlinePolylinesRef = useRef<google.maps.Polyline[]>([]);
  const geocoder = new google.maps.Geocoder();

  useEffect(() => {
    if (!map || !window.google || !visible) {
      // Clean up if map is not available or overlay is hidden
      if (dataLayerRef.current) {
        dataLayerRef.current.forEach((feature: any) => {
          dataLayerRef.current?.remove(feature);
        });
      }
      return;
    }

const sendtobackend = async () => {
  if (!workzone || workzone.length === 0) {
    return;
  }

  // Calculate center of workzone polygon
  const centerLat = workzone.reduce((sum: number, coord: any) => sum + coord.lat, 0) / workzone.length;
  const centerLng = workzone.reduce((sum: number, coord: any) => sum + coord.lng, 0) / workzone.length;

  // Reverse geocode the center point
  return new Promise((resolve, reject) => {
    geocoder.geocode(
      { location: { lat: centerLat, lng: centerLng } },
      (results, status) => {
        if (status === 'OK' && results && results[0]) {
          // Extract street name
          let streetName = 'Unknown';
          for (const component of results[0].address_components) {
            if (component.types.includes('route')) {
              streetName = component.long_name;
              break;
            }
          }

          // Send to backend
          axios.post(
            resolveApiUrl('/api/debug/config'),
            {
              road_tool_geometry: geometry,
              workzone: {
                coordinates: workzone,
                center: { lat: centerLat, lng: centerLng },
                address: results[0].formatted_address,
                street_name: streetName
              }
            }
          ).then(resolve).catch(reject);
        } else {
          // If geocoding fails, still send the workzone without address
          axios.post(
            resolveApiUrl('/api/debug/config'),
            {
              road_tool_geometry: geometry,
              workzone: {
                coordinates: workzone,
                center: { lat: centerLat, lng: centerLng }
              }
            }
          ).then(resolve).catch(reject);
        }
      }
    );
  });
};

    sendtobackend();

    // Get or create data layer for road tool
    const dataLayer = map.data as any;
    dataLayerRef.current = dataLayer;

    // Clear existing road tool features
    const existingFeatures: any[] = [];
    dataLayer.forEach((feature: any) => {
      const collection = feature.getProperty('collection');
      if (collection && collection.startsWith('road_tool_')) {
        existingFeatures.push(feature);
      }
    });
    existingFeatures.forEach(feature => dataLayer.remove(feature));

    // Clear existing centerline polylines
    centerlinePolylinesRef.current.forEach(polyline => polyline.setMap(null));
    centerlinePolylinesRef.current = [];

    if (!geometry) {
      return;
    }

    // Helper to add GeoJSON collection with collection tag
    const addRoadToolCollection = (
      geojson: GeoJSON.FeatureCollection | null | undefined,
      collectionName: string
    ) => {
      if (!geojson || !geojson.features || geojson.features.length === 0) {
        return;
      }

      try {
        // Tag features with collection name for styling
        const tagged = {
          type: geojson.type,
          features: geojson.features.map((feat: any) => ({
            ...feat,
            properties: {
              ...(feat.properties || {}),
              collection: collectionName
            }
          }))
        };

        dataLayer.addGeoJson(tagged);
      } catch (e) {
        console.warn(`Failed to add road tool ${collectionName}:`, e);
      }
    };

    // Add road tool collections (order matters - surfaces first so they render behind lines)
    addRoadToolCollection(geometry.road_surface, 'road_tool_road_surface');
    // Centerlines are handled separately as Polylines for dashed effect
    addRoadToolCollection(geometry.lane_boundaries, 'road_tool_lane_boundaries');
    addRoadToolCollection(geometry.road_edges, 'road_tool_road_edges');
    addRoadToolCollection(geometry.intersections, 'road_tool_intersections');

    // Create dashed white centerlines using Polyline overlays
    if (geometry.centerlines && geometry.centerlines.features) {
      geometry.centerlines.features.forEach((feature: any) => {
        if (feature.geometry && feature.geometry.type === 'LineString' && feature.geometry.coordinates) {
          const path = feature.geometry.coordinates.map((coord: number[]) => ({
            lat: coord[1],
            lng: coord[0]
          }));

          // Create dashed white centerline using repeating symbol pattern
          // Use a custom line segment path that repeats to create dashes
          const dashSymbol = {
            path: 'M 0,-1 L 0,1',  // Vertical line segment  // 2px horizontal line (will be scaled)
            strokeColor: '#ffffff',
            strokeWeight: 2,
            strokeOpacity: 0.9,
            scale: 5  // Makes it a 10px dash
          };

          const centerlinePolyline = new window.google.maps.Polyline({
            path: path,
            strokeColor: 'transparent',  // Hide base stroke, only show symbols
            strokeWeight: 0,
            strokeOpacity: 0,
            icons: [{
              icon: dashSymbol,
              offset: '0%',
              repeat: '20px'  // 10px dash, 10px gap
            }],
            zIndex: 10
          });

          centerlinePolyline.setMap(map);
          centerlinePolylinesRef.current.push(centerlinePolyline);
        }
      });
    }

    // Style road tool features
    dataLayer.setStyle((feature: any) => {
      const collection = feature.getProperty('collection');

      // Road surface: Light grey fill (rendered first, behind everything)
      if (collection === 'road_tool_road_surface') {
        return {
          fillColor: '#e0e0e0',  // Very light grey
          fillOpacity: 0.4,
          strokeColor: 'transparent',  // No outline
          strokeWeight: 0,
          zIndex: 1
        };
      }

      // Centerlines are now handled as Polyline overlays (not in Data Layer)

      // Lane boundaries: Yellow solid line (keep for now, can be hidden later)
      if (collection === 'road_tool_lane_boundaries') {
        return {
          strokeColor: '#ffc107',  // Yellow/Amber
          strokeWeight: 1.5,
          strokeOpacity: 0.7,
          zIndex: 9
        };
      }

      // Road edges: Thicker white lines
      if (collection === 'road_tool_road_edges') {
        return {
          strokeColor: '#ffffff',  // White
          strokeWeight: 3,  // Thicker
          strokeOpacity: 0.95,
          zIndex: 8
        };
      }

      // Intersections: Red markers
      if (collection === 'road_tool_intersections') {
        return {
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 5,
            fillColor: '#d32f2f',  // Red
            fillOpacity: 0.8,
            strokeColor: '#ffffff',
            strokeWeight: 1.5,
            zIndex: 11
          }
        };
      }

      return {};
    });

    // Make features clickable if editable (future enhancement)
    if (editable) {
      dataLayer.addListener('click', (event: any) => {
        const feature = event.feature;
        const collection = feature.getProperty('collection');
        if (collection && collection.startsWith('road_tool_')) {
          // TODO: Implement editing logic
          console.log('Road tool feature clicked:', feature);
        }
      });
    }

  }, [map, geometry, visible, editable]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (dataLayerRef.current) {
        const dataLayer = dataLayerRef.current;
        const featuresToRemove: any[] = [];
        dataLayer.forEach((feature: any) => {
          const collection = feature.getProperty('collection');
          if (collection && collection.startsWith('road_tool_')) {
            featuresToRemove.push(feature);
          }
        });
        featuresToRemove.forEach(feature => dataLayer.remove(feature));
      }
      // Clean up centerline polylines
      centerlinePolylinesRef.current.forEach(polyline => polyline.setMap(null));
      centerlinePolylinesRef.current = [];
    };
  }, []);

  // Component doesn't render anything visible - it manipulates the map directly
  return null;
};

